import React from "react";

// THE CONSOLIDATE-EACH-PART REPLY — the smallest Refine-specific presentation: the DB text arrives as PLAIN prose (the backend's
// strip_markdown removes every markdown symbol), so presentation is the UI's job. The three known headings, each on its own line,
// become bold block headings; the body of each chunk is ordinary prose; ONE key point per chunk is emphasized (bold italic — never colour
// alone): "What to work on" → the sentence that teaches the operation ("… means …" / "This is …"); "Try this" → the whole action.
// Anything that is not this three-chunk shape is returned as null so the caller keeps its ordinary rendering.
export const REFINE_HEADINGS = ["What you've established", "What to work on", "Try this"];
const HEAD_RE = new RegExp(`^\\s*(?:\\*\\*)?\\s*(${REFINE_HEADINGS.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/'/g, "['’]")).join("|")})\\s*:?\\s*(?:\\*\\*)?\\s*$`, "i");

export function parseRefineReply(text) {
  const lines = String(text || "").replace(/\*\*/g, "").split("\n");
  const chunks = [];
  let cur = null;
  for (const raw of lines) {
    const m = raw.match(HEAD_RE);
    if (m) { cur = { heading: canonical(m[1]), body: [] }; chunks.push(cur); continue; }
    if (cur && raw.trim()) cur.body.push(raw.trim());
  }
  if (chunks.length < 2 || chunks[0].heading !== REFINE_HEADINGS[0]) return null;
  return chunks.map((c) => ({ heading: c.heading, body: c.body.join(" ") }));
}

function canonical(h) {
  const n = h.toLowerCase().replace(/[’]/g, "'");
  return REFINE_HEADINGS.find((x) => x.toLowerCase() === n) || h;
}

const sentences = (s) => (s.match(/[^.!?]+[.!?]+["”’)]?|[^.!?]+$/g) || []).map((x) => x.trim()).filter(Boolean);

function keySentence(heading, body) {
  const ss = sentences(body);
  if (!ss.length) return -1;
  if (heading === REFINE_HEADINGS[2]) return -2;                                            // Try this: the whole action is the key
  if (heading === REFINE_HEADINGS[1]) {
    const k = ss.findIndex((x) => /\b(means|is called|This is|That is)\b/i.test(x));
    return k;
  }
  return -1;
}

export function RefineReply({ text }) {
  const chunks = parseRefineReply(text);
  if (!chunks) return null;
  return (
    <div data-testid="refine-reply" className="space-y-2.5">
      {chunks.map((c, i) => {
        const ss = sentences(c.body);
        const key = keySentence(c.heading, c.body);
        return (
          <section key={c.heading + i} data-testid={`refine-chunk-${i}`}>
            <h4 data-testid="refine-heading" className="text-[12px] font-semibold text-stone-900 tracking-[0.01em]">{c.heading}</h4>
            <p className="mt-0.5 text-[13.5px] leading-relaxed text-stone-800">
              {key === -2
                ? <strong data-testid="refine-key" className="font-semibold italic text-stone-900">{c.body}</strong>
                : ss.map((s, k) => (k === key
                  ? <strong key={k} data-testid="refine-key" className="font-semibold italic text-stone-900">{s} </strong>
                  : <span key={k}>{s} </span>))}
            </p>
          </section>
        );
      })}
    </div>
  );
}
